import { useState, useEffect, useCallback } from 'react';
import { useAppSelector } from './useAppStore';
import { selectAccountInfo } from 'features/account/accountSelectors';

export type FilterName =
  | 'usersFilter'
  | 'subjectsFilter'
  | 'deliveryPointsFilter'
  | 'devicesFilter'
  | 'deliveryContractsFilter'
  | 'balanceGroupsFilter';

const getFilterLocalStorageKey = (
  filterName: string,
  email: string,
  sub: string
) => {
  const localStorageKeyPrefix = `${email}_${sub}`;
  const localStorageKey = `${filterName}_${localStorageKeyPrefix}`;
  return localStorageKey;
};

export const useLocalStorageFilter = <FilterValueType>(
  filterName: FilterName
) => {
  const accountInfo = useAppSelector(selectAccountInfo);

  const [localStorageFilterLoaded, setLocalStorageFilterLoaded] =
    useState(false);
  const [localStorageFilterValue, setLocalStorageFilterValue] = useState<
    FilterValueType | undefined
  >();

  const clearOldFilters = useCallback(
    (email: string, sub: string) => {
      const localStorageItems = { ...localStorage };
      Object.keys(localStorageItems).forEach((localStorageItemKey) => {
        if (
          localStorageItemKey.includes(filterName) &&
          localStorageItemKey.includes(email) &&
          !localStorageItemKey.includes(sub)
        ) {
          localStorage.removeItem(localStorageItemKey);
        }
      });
    },
    [filterName]
  );

  useEffect(() => {
    if (
      !localStorageFilterLoaded &&
      accountInfo &&
      accountInfo.email &&
      accountInfo.sub
    ) {
      const localStorageKey = getFilterLocalStorageKey(
        filterName,
        accountInfo.email,
        accountInfo.sub
      );
      clearOldFilters(accountInfo.email, accountInfo.sub);
      const localStorageFilter = localStorage.getItem(localStorageKey);
      if (localStorageFilter) {
        const filterValue: FilterValueType = JSON.parse(localStorageFilter);
        setLocalStorageFilterValue(filterValue);
      }
      setLocalStorageFilterLoaded(true);
    }
  }, [localStorageFilterLoaded, accountInfo, clearOldFilters, filterName]);

  const saveFilter = (filterValue: FilterValueType) => {
    if (accountInfo && accountInfo.email && accountInfo.sub) {
      const localStorageKey = getFilterLocalStorageKey(
        filterName,
        accountInfo.email,
        accountInfo.sub
      );
      localStorage.setItem(localStorageKey, JSON.stringify(filterValue));
    }
  };

  return { localStorageFilterLoaded, localStorageFilterValue, saveFilter };
};
